<script lang="ts">
  import {
    SSHKey,
    GPGKey,
    Session,
  } from "../models/state";
  import { appStore } from "../stores/appStore";
  import { REST_URL } from "../utils/config";
  import { themeStore } from "../stores/themeStore";
  import { getTokenStorage, removeTokenStorage } from "../utils/localStorage";
  import type { AppApiClient } from "../api_contracts/app.client";
  import Navbar from "../components/Navbar.svelte";
  import BillingSection from "../components/billing/BillingSection.svelte";
  import { onMount } from "svelte";

  let client: AppApiClient | null = $state(null);
  let token = $state("");
  let loading = $state(false);
  let error = $state("");
  let success = $state("");

  // Profile fields
  let email = $state("");
  let firstName = $state("");
  let lastName = $state("");
  let userName = $state("");
  let bio = $state("");
  let location = $state("");
  let country = $state("");
  let avatarUrl = $state("");

  // Avatar upload fields
  let avatarUploading = $state(false);
  let avatarError = $state("");
  let avatarSuccess = $state("");
  let fileInput: HTMLInputElement;

  // Password change fields
  let activeTab = $state("profile");
  let newPassword = $state("");
  let confirmPassword = $state("");
  let passwordCode = $state("");
  let codeSent = $state(false);
  let passwordLoading = $state(false);
  let passwordError = $state("");
  let passwordSuccess = $state("");

  let sshKeys: SSHKey[] = $state([]);
  let gpgKeys: GPGKey[] = $state([]);
  let showAddSSHKey = $state(false);
  let showAddGPGKey = $state(false);
  let newSSHKeyTitle = $state("");
  let newSSHKeyContent = $state("");
  let newGPGKeyTitle = $state("");
  let newGPGKeyContent = $state("");
  let keysLoading = $state(false);
  let keysError = $state("");
  let keysSuccess = $state("");

  let sessions: Session[] = $state([]);
  let sessionsLoading = $state(false);
  let sessionsError = $state("");
  let sessionsSuccess = $state("");

  // Two-Factor Authentication
  let twoFactorEnabled = $state(false);
  let show2FASetup = $state(true);
  let showEnableTwoFactor = $state(false);
  let otpSecret = $state("");
  let otpQrCode = $state("");
  let otpVerificationCode = $state("");
  let recoveryCodes: string[] = $state([]);
  let showRecoveryCodes = $state(false);
  let twoFactorLoading = $state(false);
  let twoFactorError = $state("");
  let twoFactorSuccess = $state("");

  // Add these variables for the new 2FA section
  let twoFactorSecret = $state("");
  let twoFactorQrCode = $state("");
  let totpCode = $state("");
  let disableTotpCode = $state("");

  // Social Links
  interface SocialLink {
    id: string;
    platform: string;
    url: string;
    displayOrder: number;
  }
  let socialLinks: SocialLink[] = $state([]);
  let socialLinksLoading = $state(false);
  let socialLinksError = $state("");
  let socialLinksSuccess = $state("");
  let socialLinksLoaded = $state(false);

  // User Emails
  interface UserEmail {
    id: string;
    email: string;
    isPrimary: boolean;
    isPublic: boolean;
    isVerified: boolean;
  }
  let userEmails: UserEmail[] = $state([]);
  let emailsLoading = $state(false);
  let emailsError = $state("");
  let emailsSuccess = $state("");
  let newEmailAddress = $state("");
  let newEmailPublic = $state(false);
  let emailsLoaded = $state(false);
  let verifyingEmailId = $state("");
  let verificationCode = $state("");

  // Theme
  let currentTheme = $state<"light" | "dark">("dark");
  themeStore.subscribe((theme) => {
    currentTheme = theme;
  });

  // Subscribe to store
  appStore.subscribe((state) => {
    client = state.client;
    token = state.token;
  });

  // Check authentication and load profile
  onMount(async () => {
    const storedToken = getTokenStorage();
    if (!storedToken) {
      window.location.hash = "/login";
      return;
    }

    await loadProfile();
  });

  async function loadProfile() {
    if (!client) {
      error = "Client not initialized";
      return;
    }

    try {
      loading = true;
      error = "";

      const result = await client.getUserProfile({ token });

      if (result.response.response.successFull) {
        console.log("Profile loaded:", result.response.profile);
        console.log("2FA field value:", result.response.profile.is2FaEnabled);

        email = result.response.profile.email || "";
        firstName = result.response.profile.firstName || "";
        lastName = result.response.profile.lastName || "";
        userName = result.response.profile.userName || "";
        bio = result.response.profile.bio || "";
        location = result.response.profile.location || "";
        country = result.response.profile.country || "";
        avatarUrl = result.response.profile.avatarUrl || "";
        twoFactorEnabled = result.response.profile.is2FaEnabled || false;

        console.log("twoFactorEnabled set to:", twoFactorEnabled);
      } else {
        error = result.response.response.reason || "Failed to load profile";
      }
    } catch (e: any) {
      error = e.message || "Error loading profile";
      console.error("Load profile error:", e);
    } finally {
      loading = false;
    }
  }

  async function handleAvatarUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      avatarError =
        "Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed";
      setTimeout(() => (avatarError = ""), 3000);
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      avatarError = "File size exceeds 5MB limit";
      setTimeout(() => (avatarError = ""), 3000);
      return;
    }

    try {
      avatarUploading = true;
      avatarError = "";
      avatarSuccess = "";

      const formData = new FormData();
      formData.append("avatar", file);

      const response = await fetch(`${REST_URL}/api/v1/user/avatar`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.error) {
        avatarError = data.message || "Failed to upload avatar";
      } else {
        avatarSuccess = "Avatar uploaded successfully!";
        avatarUrl = data.avatar_url;
        setTimeout(() => (avatarSuccess = ""), 3000);
      }
    } catch (e: any) {
      avatarError = e.message || "Error uploading avatar";
      console.error("Avatar upload error:", e);
    } finally {
      avatarUploading = false;
      // Reset file input
      if (fileInput) fileInput.value = "";
    }
  }

  async function handleDeleteAvatar() {
    if (!confirm("Are you sure you want to delete your avatar?")) {
      return;
    }

    try {
      avatarUploading = true;
      avatarError = "";

      const response = await fetch(`${REST_URL}/api/v1/user/avatar`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.error) {
        avatarError = data.message || "Failed to delete avatar";
      } else {
        avatarSuccess = "Avatar deleted successfully!";
        avatarUrl = "";
        setTimeout(() => (avatarSuccess = ""), 3000);
      }
    } catch (e: any) {
      avatarError = e.message || "Error deleting avatar";
      console.error("Avatar delete error:", e);
    } finally {
      avatarUploading = false;
    }
  }

  async function handleUpdateProfile(e: Event) {
    e.preventDefault();

    if (!client) {
      error = "Client not initialized";
      return;
    }

    try {
      loading = true;
      error = "";
      success = "";

      const result = await client.updateProfile({
        token,
        firstName,
        lastName,
        userName,
        bio,
        location,
        country,
        theme: currentTheme,
      });

      if (result.response.successFull) {
        success = "Profile updated successfully!";
        setTimeout(() => (success = ""), 3000);
      } else {
        error = result.response.reason || "Failed to update profile";
      }
    } catch (e: any) {
      error = e.message || "Error updating profile";
      console.error("Update profile error:", e);
    } finally {
      loading = false;
    }
  }

  // Social Links Management
  async function loadSocialLinks() {
    if (!client || !token || socialLinksLoaded) return;
    try {
      socialLinksLoading = true;
      socialLinksError = "";
      const result = await client.getSocialLinks({ token });
      if (result.response.successFull) {
        socialLinks = (result.response.links || []).map((link: any) => ({
          id: link.id,
          platform: link.platform,
          url: link.url,
          displayOrder: link.displayOrder
        }));
      }
    } catch (e: any) {
      socialLinksError = e.message || "Failed to load social links";
    } finally {
      socialLinksLoading = false;
      socialLinksLoaded = true;
    }
  }

  async function addSocialLink() {
    if (!client || !token) return;
    // Add a new empty social link
    const newLink = { id: "", platform: "auto", url: "", displayOrder: socialLinks.length };
    socialLinks = [...socialLinks, newLink];
  }

  async function saveSocialLink(index: number) {
    if (!client || !token) return;
    const link = socialLinks[index];
    if (!link.url) {
      socialLinksError = "URL is required";
      setTimeout(() => (socialLinksError = ""), 3000);
      return;
    }

    try {
      socialLinksLoading = true;
      socialLinksError = "";
      socialLinksSuccess = "";

      if (link.id) {
        // Update existing link
        const result = await client.updateSocialLink({
          token,
          linkId: link.id,
          platform: link.platform,
          url: link.url,
          displayOrder: link.displayOrder
        });
        if (result.response.successFull) {
          socialLinksSuccess = "Social link updated!";
          setTimeout(() => (socialLinksSuccess = ""), 3000);
        }
      } else {
        // Add new link
        const result = await client.addSocialLink({
          token,
          platform: link.platform,
          url: link.url,
          displayOrder: link.displayOrder
        });
        if (result.response.successFull && result.response.link) {
          socialLinks[index] = {
            id: result.response.link.id,
            platform: result.response.link.platform,
            url: result.response.link.url,
            displayOrder: result.response.link.displayOrder
          };
          socialLinksSuccess = "Social link added!";
          setTimeout(() => (socialLinksSuccess = ""), 3000);
        }
      }
    } catch (e: any) {
      socialLinksError = e.message || "Failed to save social link";
      setTimeout(() => (socialLinksError = ""), 3000);
    } finally {
      socialLinksLoading = false;
    }
  }

  async function deleteSocialLink(index: number) {
    if (!client || !token) return;
    const link = socialLinks[index];

    if (!link.id) {
      // Just remove from array if not saved yet
      socialLinks = socialLinks.filter((_, i) => i !== index);
      return;
    }

    try {
      socialLinksLoading = true;
      const result = await client.deleteSocialLink({ token, linkId: link.id });
      if (result.response.successFull) {
        socialLinks = socialLinks.filter((_, i) => i !== index);
        socialLinksSuccess = "Social link deleted!";
        setTimeout(() => (socialLinksSuccess = ""), 3000);
      }
    } catch (e: any) {
      socialLinksError = e.message || "Failed to delete social link";
      setTimeout(() => (socialLinksError = ""), 3000);
    } finally {
      socialLinksLoading = false;
    }
  }

  // Email Management
  async function loadUserEmails(force = false) {
    if (!client || !token) return;
    if (emailsLoaded && !force) return;
    try {
      emailsLoading = true;
      emailsError = "";
      const result = await client.getUserEmails({ token });
      if (result.response.successFull) {
        userEmails = (result.response.emails || []).map((email: any) => ({
          id: email.id,
          email: email.email,
          isPrimary: email.isPrimary,
          isPublic: email.isPublic,
          isVerified: email.isVerified
        }));
      }
    } catch (e: any) {
      emailsError = e.message || "Failed to load emails";
    } finally {
      emailsLoading = false;
      emailsLoaded = true;
    }
  }

  async function addNewEmail() {
    if (!client || !token) return;
    if (!newEmailAddress) {
      emailsError = "Email address is required";
      setTimeout(() => (emailsError = ""), 3000);
      return;
    }

    try {
      emailsLoading = true;
      emailsError = "";
      emailsSuccess = "";
      const result = await client.addUserEmail({
        token,
        email: newEmailAddress,
        isPublic: newEmailPublic
      });
      if (result.response.successFull) {
        await loadUserEmails(true); // Force reload
        newEmailAddress = "";
        newEmailPublic = false;
        emailsSuccess = "Email added! Please verify your email.";
        setTimeout(() => (emailsSuccess = ""), 5000);
      } else {
        emailsError = result.response.reason || "Failed to add email";
        setTimeout(() => (emailsError = ""), 3000);
      }
    } catch (e: any) {
      emailsError = e.message || "Failed to add email";
      setTimeout(() => (emailsError = ""), 3000);
    } finally {
      emailsLoading = false;
    }
  }

  async function toggleEmailPublic(emailId: string, isPublic: boolean) {
    if (!client || !token) return;
    try {
      emailsLoading = true;
      const result = await client.updateEmailPublicStatus({ token, emailId, isPublic });
      if (result.response.successFull) {
        await loadUserEmails(true); // Force reload
        emailsSuccess = `Email visibility updated!`;
        setTimeout(() => (emailsSuccess = ""), 3000);
      }
    } catch (e: any) {
      emailsError = e.message || "Failed to update email visibility";
      setTimeout(() => (emailsError = ""), 3000);
    } finally {
      emailsLoading = false;
    }
  }

  async function setPrimaryEmail(emailId: string) {
    if (!client || !token) return;
    try {
      emailsLoading = true;
      const result = await client.setPrimaryEmail({ token, emailId });
      if (result.response.successFull) {
        await loadUserEmails(true); // Force reload
        emailsSuccess = "Primary email updated!";
        setTimeout(() => (emailsSuccess = ""), 3000);
      }
    } catch (e: any) {
      emailsError = e.message || "Failed to set primary email";
      setTimeout(() => (emailsError = ""), 3000);
    } finally {
      emailsLoading = false;
    }
  }

  async function deleteEmail(emailId: string) {
    if (!client || !token) return;
    if (!confirm("Are you sure you want to delete this email?")) return;

    try {
      emailsLoading = true;
      const result = await client.deleteUserEmail({ token, emailId });
      if (result.response.successFull) {
        await loadUserEmails(true); // Force reload
        emailsSuccess = "Email deleted!";
        setTimeout(() => (emailsSuccess = ""), 3000);
      } else {
        emailsError = result.response.reason || "Failed to delete email";
        setTimeout(() => (emailsError = ""), 3000);
      }
    } catch (e: any) {
      emailsError = e.message || "Failed to delete email";
      setTimeout(() => (emailsError = ""), 3000);
    } finally {
      emailsLoading = false;
    }
  }

  async function verifyEmail(emailId: string) {
    if (!client || !token) return;
    if (!verificationCode) {
      emailsError = "Verification code is required";
      setTimeout(() => (emailsError = ""), 3000);
      return;
    }

    try {
      emailsLoading = true;
      const result = await client.verifyUserEmail({ token, emailId, verificationCode });
      if (result.response.successFull) {
        await loadUserEmails(true); // Force reload
        verifyingEmailId = "";
        verificationCode = "";
        emailsSuccess = "Email verified successfully!";
        setTimeout(() => (emailsSuccess = ""), 3000);
      } else {
        emailsError = result.response.reason || "Failed to verify email";
        setTimeout(() => (emailsError = ""), 3000);
      }
    } catch (e: any) {
      emailsError = e.message || "Failed to verify email";
      setTimeout(() => (emailsError = ""), 3000);
    } finally {
      emailsLoading = false;
    }
  }

  async function updateTheme(theme: 'light' | 'dark') {
    // Update UI immediately
    themeStore.setTheme(theme);

    // Save to backend
    if (!client || !token) return;

    try {
      await client.updateProfile({
        token,
        firstName,
        lastName,
        userName,
        bio,
        location,
        country,
        theme,
      });
      console.log('Theme preference saved to backend');
    } catch (e: any) {
      console.error('Error saving theme preference:', e);
      // Don't show error to user as theme is already applied locally
    }
  }

  async function requestPasswordCode() {
    if (!client) {
      passwordError = "Client not initialized";
      return;
    }

    try {
      passwordLoading = true;
      passwordError = "";

      const result = await client.requestPasswordChangeCode({ token });

      if (result.response.successFull) {
        codeSent = true;
        passwordSuccess = "Password change code sent to your email!";
        setTimeout(() => (passwordSuccess = ""), 3000);
      } else {
        passwordError = result.response.reason || "Failed to send code";
      }
    } catch (e: any) {
      passwordError = e.message || "Error requesting code";
      console.error("Request code error:", e);
    } finally {
      passwordLoading = false;
    }
  }

  async function handleChangePassword(e: Event) {
    e.preventDefault();

    if (!client) {
      passwordError = "Client not initialized";
      return;
    }

    if (newPassword !== confirmPassword) {
      passwordError = "Passwords do not match";
      return;
    }

    if (newPassword.length < 8) {
      passwordError = "Password must be at least 8 characters";
      return;
    }

    if (!passwordCode) {
      passwordError = "Please enter the code from your email";
      return;
    }

    try {
      passwordLoading = true;
      passwordError = "";

      const result = await client.changePassword({
        token,
        code: passwordCode,
        newPassword,
      });

      if (result.response.successFull) {
        passwordSuccess = "Password changed successfully!";
        newPassword = "";
        confirmPassword = "";
        passwordCode = "";
        codeSent = false;
        setTimeout(() => (passwordSuccess = ""), 3000);
      } else {
        passwordError = result.response.reason || "Failed to change password";
      }
    } catch (e: any) {
      passwordError = e.message || "Error changing password";
      console.error("Change password error:", e);
    } finally {
      passwordLoading = false;
    }
  }

  // Load SSH and GPG keys
  async function loadKeys() {
    if (!client || !token) return;

    try {
      // Load SSH keys
      const sshResult = await client.getSSHKeys({ token });
      if (sshResult.response.successFull && sshResult.response.keys) {
        sshKeys = sshResult.response.keys.map((key) => ({
          id: key.id || "",
          title: key.title || "",
          key: key.publicKey || "",
          fingerprint: key.fingerprint || "",
          createdAt: key.createdAt
            ? new Date(Number(key.createdAt.seconds) * 1000).toISOString().split("T")[0]
            : "",
        }));
      }

      // Load GPG keys
      const gpgResult = await client.getGPGKeys({ token });
      if (gpgResult.response.successFull && gpgResult.response.keys) {
        gpgKeys = gpgResult.response.keys.map((key) => ({
          id: key.id || "",
          title: key.title || "",
          keyId: key.keyId || "",
          publicKey: key.publicKey || "",
          createdAt: key.createdAt
            ? new Date(Number(key.createdAt.seconds) * 1000).toISOString().split("T")[0]
            : "",
        }));
      }
    } catch (e: any) {
      console.error("Error loading keys:", e);
    }
  }

  async function handleAddSSHKey() {
    if (!newSSHKeyTitle.trim() || !newSSHKeyContent.trim()) {
      keysError = "Please provide both title and SSH key";
      setTimeout(() => (keysError = ""), 3000);
      return;
    }

    if (!client || !token) {
      keysError = "Not authenticated";
      setTimeout(() => (keysError = ""), 3000);
      return;
    }

    try {
      keysLoading = true;
      keysError = "";

      const result = await client.addSSHKey({
        token,
        title: newSSHKeyTitle,
        publicKey: newSSHKeyContent,
      });

      if (result.response.successFull && result.response.key) {
        const newKey: SSHKey = {
          id: result.response.key.id || "",
          title: result.response.key.title || "",
          key: result.response.key.publicKey || "",
          fingerprint: result.response.key.fingerprint || "",
          createdAt: result.response.key.createdAt
            ? new Date(Number(result.response.key.createdAt.seconds) * 1000).toISOString().split("T")[0]
            : "",
        };

        sshKeys = [...sshKeys, newKey];
        keysSuccess = "SSH key added successfully!";
        newSSHKeyTitle = "";
        newSSHKeyContent = "";
        showAddSSHKey = false;
        setTimeout(() => (keysSuccess = ""), 3000);
      } else {
        keysError = result.response.reason || "Failed to add SSH key";
        setTimeout(() => (keysError = ""), 3000);
      }
    } catch (e: any) {
      keysError = e.message || "Error adding SSH key";
      setTimeout(() => (keysError = ""), 3000);
    } finally {
      keysLoading = false;
    }
  }

  async function handleDeleteSSHKey(keyId: string) {
    if (!confirm("Are you sure you want to delete this SSH key?")) {
      return;
    }

    if (!client || !token) {
      keysError = "Not authenticated";
      setTimeout(() => (keysError = ""), 3000);
      return;
    }

    try {
      keysLoading = true;
      keysError = "";

      const result = await client.deleteSSHKey({ token, keyId });

      if (result.response.successFull) {
        sshKeys = sshKeys.filter((k) => k.id !== keyId);
        keysSuccess = "SSH key deleted successfully!";
        setTimeout(() => (keysSuccess = ""), 3000);
      } else {
        keysError = result.response.reason || "Failed to delete SSH key";
        setTimeout(() => (keysError = ""), 3000);
      }
    } catch (e: any) {
      keysError = e.message || "Error deleting SSH key";
      setTimeout(() => (keysError = ""), 3000);
    } finally {
      keysLoading = false;
    }
  }

  async function handleAddGPGKey() {
    if (!newGPGKeyTitle.trim() || !newGPGKeyContent.trim()) {
      keysError = "Please provide both title and GPG key";
      setTimeout(() => (keysError = ""), 3000);
      return;
    }

    if (!client || !token) {
      keysError = "Not authenticated";
      setTimeout(() => (keysError = ""), 3000);
      return;
    }

    try {
      keysLoading = true;
      keysError = "";

      const result = await client.addGPGKey({
        token,
        title: newGPGKeyTitle,
        publicKey: newGPGKeyContent,
      });

      if (result.response.successFull && result.response.key) {
        const newKey: GPGKey = {
          id: result.response.key.id || "",
          title: result.response.key.title || "",
          keyId: result.response.key.keyId || "",
          publicKey: result.response.key.publicKey || "",
          createdAt: result.response.key.createdAt
            ? new Date(Number(result.response.key.createdAt.seconds) * 1000).toISOString().split("T")[0]
            : "",
        };

        gpgKeys = [...gpgKeys, newKey];
        keysSuccess = "GPG key added successfully!";
        newGPGKeyTitle = "";
        newGPGKeyContent = "";
        showAddGPGKey = false;
        setTimeout(() => (keysSuccess = ""), 3000);
      } else {
        keysError = result.response.reason || "Failed to add GPG key";
        setTimeout(() => (keysError = ""), 3000);
      }
    } catch (e: any) {
      keysError = e.message || "Error adding GPG key";
      setTimeout(() => (keysError = ""), 3000);
    } finally {
      keysLoading = false;
    }
  }

  async function handleDeleteGPGKey(keyId: string) {
    if (!confirm("Are you sure you want to delete this GPG key?")) {
      return;
    }

    if (!client || !token) {
      keysError = "Not authenticated";
      setTimeout(() => (keysError = ""), 3000);
      return;
    }

    try {
      keysLoading = true;
      keysError = "";

      const result = await client.deleteGPGKey({ token, keyId });

      if (result.response.successFull) {
        gpgKeys = gpgKeys.filter((k) => k.id !== keyId);
        keysSuccess = "GPG key deleted successfully!";
        setTimeout(() => (keysSuccess = ""), 3000);
      } else {
        keysError = result.response.reason || "Failed to delete GPG key";
        setTimeout(() => (keysError = ""), 3000);
      }
    } catch (e: any) {
      keysError = e.message || "Error deleting GPG key";
      setTimeout(() => (keysError = ""), 3000);
    } finally {
      keysLoading = false;
    }
  }

  // Load sessions
  async function loadSessions() {
    if (!client || !token) return;

    try {
      sessionsLoading = true;
      sessionsError = "";

      const result = await client.getUserSessions({ token });

      if (result.response.successFull && result.response.sessions) {
        // Convert from gRPC format to local format
        sessions = result.response.sessions.map((s) => {
          const lastAccessed = s.lastAccessedAt
            ? new Date(Number(s.lastAccessedAt.seconds) * 1000)
            : new Date();
          const now = new Date();
          const diffMs = now.getTime() - lastAccessed.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const diffHours = Math.floor(diffMs / 3600000);
          const diffDays = Math.floor(diffMs / 86400000);

          let lastAccessedStr = "";
          if (diffMins < 1) lastAccessedStr = "Just now";
          else if (diffMins < 60)
            lastAccessedStr = `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
          else if (diffHours < 24)
            lastAccessedStr = `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
          else
            lastAccessedStr = `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

          return {
            id: s.id,
            device: s.deviceName,
            browser: s.browserName,
            os: s.osName,
            location: s.location,
            ip: s.ipAddress,
            lastAccessed: lastAccessedStr,
            current: s.isCurrent,
          };
        });
      } else {
        sessionsError = result.response.reason || "Failed to load sessions";
      }
    } catch (e: any) {
      sessionsError = e.message || "Error loading sessions";
    } finally {
      sessionsLoading = false;
    }
  }

  async function handleRevokeSession(sessionId: string) {
    if (!client || !token) return;

    const session = sessions.find((s) => s.id === sessionId);
    const isCurrentSession = session?.current || false;

    // Different confirmation message for current session
    const confirmMessage = isCurrentSession
      ? "Are you sure you want to revoke your current session? You will be logged out immediately."
      : "Are you sure you want to revoke this session? You will need to log in again from that device.";

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      sessionsLoading = true;
      sessionsError = "";

      const result = await client.revokeSession({
        token,
        sessionId,
      });

      if (result.response.successFull) {
        // If current session was revoked, log out the user
        if (isCurrentSession) {
          removeTokenStorage();
          window.location.href = "/login";
          return;
        }

        // Remove from local list
        sessions = sessions.filter((s) => s.id !== sessionId);
        sessionsSuccess = "Session revoked successfully!";
        setTimeout(() => (sessionsSuccess = ""), 3000);
      } else {
        sessionsError = result.response.reason || "Failed to revoke session";
      }
    } catch (e: any) {
      sessionsError = e.message || "Error revoking session";
    } finally {
      sessionsLoading = false;
    }
  }

  async function handleRevokeAllSessions() {
    if (!client || !token) return;

    if (
      !confirm(
        "Are you sure you want to revoke all other sessions? You will need to log in again from all other devices.",
      )
    ) {
      return;
    }

    try {
      sessionsLoading = true;
      sessionsError = "";

      const result = await client.revokeAllSessions({ token });

      if (result.response.successFull) {
        // Keep only current session
        sessions = sessions.filter((s) => s.current);
        sessionsSuccess = "All other sessions revoked successfully!";
        setTimeout(() => (sessionsSuccess = ""), 3000);
      } else {
        sessionsError = result.response.reason || "Failed to revoke sessions";
      }
    } catch (e: any) {
      sessionsError = e.message || "Error revoking sessions";
    } finally {
      sessionsLoading = false;
    }
  }


  // Two-Factor Authentication functions
  async function handleEnableTwoFactor() {
    if (!client) {
      twoFactorError = "Client not initialized";
      return;
    }

    try {
      twoFactorLoading = true;
      twoFactorError = "";

      const result = await client.enable2FA({ token });

      console.log("Enable 2FA response:", result);

      if (result.response.successFull) {
        otpSecret = result.response.secret || "";
        otpQrCode = `data:image/png;base64,${result.response.qrCode}`;
        recoveryCodes = result.response.backupCodes || [];

        console.log("QR Code:", otpQrCode.substring(0, 50) + "...");
        console.log("Secret:", otpSecret);
        console.log("Backup codes count:", recoveryCodes.length);

        // Set the new variables for the new 2FA section
        twoFactorSecret = otpSecret;
        twoFactorQrCode = otpQrCode;

        showEnableTwoFactor = true;
        show2FASetup = true;

        console.log("twoFactorQrCode set to:", twoFactorQrCode.substring(0, 50) + "...");
      } else {
        twoFactorError = result.response.reason || "Failed to enable 2FA";
      }
    } catch (e: any) {
      twoFactorError =
        e.message || "Error enabling two-factor authentication";
    } finally {
      twoFactorLoading = false;
    }
  }

  async function handleVerifyAndEnableTwoFactor() {
    if (!client) {
      twoFactorError = "Client not initialized";
      return;
    }

    if (!otpVerificationCode.trim()) {
      twoFactorError = "Please enter the verification code";
      setTimeout(() => (twoFactorError = ""), 3000);
      return;
    }

    if (otpVerificationCode.length !== 6) {
      twoFactorError = "Verification code must be 6 digits";
      setTimeout(() => (twoFactorError = ""), 3000);
      return;
    }

    try {
      twoFactorLoading = true;
      twoFactorError = "";

      const result = await client.verify2FA({ token, code: otpVerificationCode });

      if (result.response.successFull) {
        twoFactorEnabled = true;
        showEnableTwoFactor = false;
        showRecoveryCodes = true;
        show2FASetup = false;
        otpVerificationCode = "";
        twoFactorSuccess = "Two-factor authentication enabled successfully!";
        setTimeout(() => (twoFactorSuccess = ""), 3000);
      } else {
        twoFactorError = result.response.reason || "Invalid verification code";
      }
    } catch (e: any) {
      twoFactorError = e.message || "Error verifying code";
    } finally {
      twoFactorLoading = false;
    }
  }

  async function handleDisableTwoFactor() {
    if (!client) {
      twoFactorError = "Client not initialized";
      return;
    }

    if (!disableTotpCode.trim()) {
      twoFactorError = "Please enter the verification code";
      setTimeout(() => (twoFactorError = ""), 3000);
      return;
    }

    if (!confirm(
      "Are you sure you want to disable two-factor authentication? This will make your account less secure.",
    )) {
      return;
    }

    try {
      twoFactorLoading = true;
      twoFactorError = "";

      const result = await client.disable2FA({ token, code: disableTotpCode });

      if (result.response.successFull) {
        twoFactorEnabled = false;
        recoveryCodes = [];
        otpSecret = "";
        otpQrCode = "";
        disableTotpCode = "";
        twoFactorSuccess = "Two-factor authentication disabled";
        setTimeout(() => (twoFactorSuccess = ""), 3000);
      } else {
        twoFactorError = result.response.reason || "Failed to disable 2FA";
      }
    } catch (e: any) {
      twoFactorError = e.message || "Error disabling two-factor authentication";
    } finally {
      twoFactorLoading = false;
    }
  }

  async function handleRegenerateRecoveryCodes() {
    if (
      !confirm(
        "Are you sure you want to regenerate recovery codes? Your old codes will no longer work.",
      )
    ) {
      return;
    }

    try {
      twoFactorLoading = true;
      twoFactorError = "";

      // TODO: Implement API call to regenerate recovery codes
      recoveryCodes = Array.from({ length: 10 }, () =>
        Math.random().toString(36).substring(2, 10).toUpperCase(),
      );

      showRecoveryCodes = true;
      twoFactorSuccess = "Recovery codes regenerated successfully!";
      setTimeout(() => (twoFactorSuccess = ""), 3000);
    } catch (e: any) {
      twoFactorError = e.message || "Error regenerating recovery codes";
    } finally {
      twoFactorLoading = false;
    }
  }

  function handleDownloadRecoveryCodes() {
    const codesText = recoveryCodes.join("\n");
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    twoFactorSuccess = "Recovery codes downloaded";
    setTimeout(() => (twoFactorSuccess = ""), 3000);
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  function enable2FA() {
    handleVerifyAndEnableTwoFactor();
  }

  function disable2FA() {
    handleDisableTwoFactor();
  }

  // Load data when tabs are opened
  $effect(() => {
    if (activeTab === "profile") {
      if (!socialLinksLoaded && !socialLinksLoading) {
        loadSocialLinks();
      }
      if (!emailsLoaded && !emailsLoading) {
        loadUserEmails();
      }
    }
    if (activeTab === "security") {
      if (sshKeys.length === 0 && gpgKeys.length === 0 && !keysLoading) {
        loadKeys();
      }
    }
    if (activeTab === "session") {
      if (sessions.length === 0 && !sessionsLoading) {
        loadSessions();
      }
    }
  });

  function downloadRecoveryCodes() {
    handleDownloadRecoveryCodes();
  }

  function generateNewRecoveryCodes(
    event: MouseEvent & { currentTarget: EventTarget & HTMLButtonElement },
  ) {
    throw new Error("Function not implemented.");
  }

  function copyRecoveryCodes() {
    const codesText = recoveryCodes.join("\n");
    copyToClipboard(codesText);
    twoFactorSuccess = "Recovery codes copied to clipboard";
    setTimeout(() => (twoFactorSuccess = ""), 3000);
  }
</script>

<Navbar />

<div class="min-h-screen bg-gray-50 dark:bg-gray-950 py-8">
  <div class="container mx-auto px-4 max-w-7xl">
    <h1 class="text-3xl font-light mb-6 text-gray-900 dark:text-gray-100">
      Account Settings
    </h1>

    <div class="flex flex-col lg:flex-row gap-8">
      <!-- Sidebar Navigation -->
      <nav
        class="w-full lg:w-1/5 border-b lg:border-r border-gray-200 dark:border-gray-700 lg:border-b-0 pb-4 lg:pb-0"
      >
        <div class="lg:sticky lg:top-8 text-sm">
          <h3
            class="text-xs text-gray-500 dark:text-gray-400 uppercase mb-2 ml-3 hidden lg:block"
          >
            Account
          </h3>
          <div
            class="settings-link w-full text-left {activeTab === 'profile'
              ? 'active'
              : ''}"
            onclick={() => (activeTab = "profile")}
          >
            Profile
          </div>
          <div
            class="settings-link w-full text-left {activeTab === 'security'
              ? 'active'
              : ''}"
            onclick={() => (activeTab = "security")}
          >
            Account Security
          </div>
          <div
            class="settings-link w-full text-left {activeTab === 'session'
              ? 'active'
              : ''}"
            onclick={() => (activeTab = "session")}
          >
            Sessions
          </div>
          <div
            class="settings-link w-full text-left {activeTab === 'appearance'
              ? 'active'
              : ''}"
            onclick={() => (activeTab = "appearance")}
          >
            Appearance
          </div>
          <div
            class="settings-link w-full text-left {activeTab === 'billing'
              ? 'active'
              : ''}"
            onclick={() => (activeTab = "billing")}
          >
            Billing
          </div>
        </div>
      </nav>

      <!-- Main Content -->
      <main class="w-full lg:w-4/5 space-y-8">
        {#if error}
          <div
            class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
          >
            <p class="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        {/if}

        {#if success}
          <div
            class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
          >
            <p class="text-sm text-green-600 dark:text-green-400">{success}</p>
          </div>
        {/if}

        {#if loading}
          <div class="text-center py-8">
            <p class="text-gray-600 dark:text-gray-400">Loading profile...</p>
          </div>
        {:else}
          <!-- Public Profile Section -->
          {#if activeTab === "profile"}
            <section
              class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            >
              <h2
                class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 rounded-t-lg text-gray-900 dark:text-gray-100"
              >
                Public Profile
              </h2>
              <div class="p-6">
                <form onsubmit={handleUpdateProfile} class="space-y-6">
                  <!-- Email (readonly) -->
                  <div>
                    <label
                      for="email"
                      class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      bind:value={email}
                      disabled
                      class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-500"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Email cannot be changed
                    </p>
                  </div>

                  <!-- Username -->
                  <div>
                    <label
                      for="userName"
                      class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >
                      Username
                    </label>
                    <input
                      type="text"
                      id="userName"
                      bind:value={userName}
                      disabled={loading}
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      required
                    />
                  </div>

                  <!-- First Name and Last Name -->
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        for="firstName"
                        class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        bind:value={firstName}
                        disabled={loading}
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                        required
                      />
                    </div>
                    <div>
                      <label
                        for="lastName"
                        class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        bind:value={lastName}
                        disabled={loading}
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                        required
                      />
                    </div>
                  </div>

                  <!-- Bio -->
                  <div>
                    <label
                      for="bio"
                      class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >
                      Bio (Optional)
                    </label>
                    <textarea
                      id="bio"
                      rows="3"
                      bind:value={bio}
                      disabled={loading}
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      placeholder="Tell us a little bit about yourself..."
                    ></textarea>
                  </div>

                  <!-- Location -->
                  <div>
                    <label
                      for="location"
                      class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >
                      Location (Optional)
                    </label>
                    <input
                      type="text"
                      id="location"
                      bind:value={location}
                      disabled={loading}
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      placeholder="City, Country"
                    />
                  </div>

                  <!-- Country -->
                  <div>
                    <label
                      for="country"
                      class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >
                      Country
                    </label>
                    <select
                      id="country"
                      bind:value={country}
                      disabled={loading}
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    >
                      <option value="">Select your country</option>
                      <option value="US">United States</option>
                      <option value="GB">United Kingdom</option>
                      <option value="CA">Canada</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                      <option value="KE">Kenya</option>
                      <option value="NG">Nigeria</option>
                      <option value="ZA">South Africa</option>
                      <option value="IN">India</option>
                      <option value="JP">Japan</option>
                      <option value="BR">Brazil</option>
                      <option value="MX">Mexico</option>
                      <option value="ES">Spain</option>
                      <option value="IT">Italy</option>
                      <option value="NL">Netherlands</option>
                      <option value="SE">Sweden</option>
                      <option value="NO">Norway</option>
                      <option value="DK">Denmark</option>
                      <option value="FI">Finland</option>
                    </select>
                  </div>

                  <!-- Avatar section -->
                  <div
                    class="pt-4 border-t border-gray-200 dark:border-gray-700"
                  >
                    {#if avatarError}
                      <div
                        class="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                      >
                        <p class="text-sm text-red-600 dark:text-red-400">
                          {avatarError}
                        </p>
                      </div>
                    {/if}

                    {#if avatarSuccess}
                      <div
                        class="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
                      >
                        <p class="text-sm text-green-600 dark:text-green-400">
                          {avatarSuccess}
                        </p>
                      </div>
                    {/if}

                    <div class="flex items-center gap-4">
                      {#if avatarUrl}
                        <img
                          src={avatarUrl}
                          alt="User avatar"
                          class="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                        />
                      {:else}
                        <div
                          class="w-16 h-16 rounded-full bg-indigo-600 flex items-center justify-center text-white text-2xl font-bold"
                        >
                          {firstName.charAt(0).toUpperCase()}{lastName
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      {/if}

                      <div class="flex gap-2">
                        <input
                          type="file"
                          bind:this={fileInput}
                          onchange={handleAvatarUpload}
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          disabled={avatarUploading}
                          class="hidden"
                          id="avatar-upload"
                        />
                        <label
                          for="avatar-upload"
                          class="cursor-pointer py-1.5 px-3 border border-gray-300 rounded-md bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600 {avatarUploading
                            ? 'opacity-50 cursor-not-allowed'
                            : ''}"
                        >
                          {avatarUploading ? "Uploading..." : "Change Avatar"}
                        </label>

                        {#if avatarUrl}
                          <button
                            type="button"
                            onclick={handleDeleteAvatar}
                            disabled={avatarUploading}
                            class="py-1.5 px-3 border border-red-300 rounded-md bg-white text-red-600 text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-red-400 dark:hover:bg-gray-600 dark:border-red-800"
                          >
                            Delete
                          </button>
                        {/if}
                      </div>
                    </div>
                    <p class="text-xs text-gray-500 mt-2">
                      Max file size: 5MB. Accepted formats: JPEG, PNG, GIF, WebP
                    </p>
                  </div>

                  <div class="pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      class="bg-green-600 text-white py-2 px-4 rounded-md font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-800"
                    >
                      {loading ? "Updating..." : "Update Profile"}
                    </button>
                  </div>
                </form>
              </div>
            </section>

            <!-- Social Media Links Section -->
            {#if activeTab === "profile"}
              <section
                class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 mt-6"
              >
                <h2
                  class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 rounded-t-lg text-gray-900 dark:text-gray-100"
                >
                  Social Media Links
                </h2>
                <div class="p-6">
                  {#if socialLinksError}
                    <div class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p class="text-sm text-red-600 dark:text-red-400">{socialLinksError}</p>
                    </div>
                  {/if}

                  {#if socialLinksSuccess}
                    <div class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                      <p class="text-sm text-green-600 dark:text-green-400">{socialLinksSuccess}</p>
                    </div>
                  {/if}

                  <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Add links to your social media profiles. Platforms are automatically detected from URLs.
                  </p>

                  <div class="space-y-3">
                    {#each socialLinks as link, index}
                      <div class="flex items-center gap-2">
                        <input
                          type="url"
                          bind:value={link.url}
                          placeholder="https://github.com/username"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                          onblur={() => saveSocialLink(index)}
                        />
                        <button
                          type="button"
                          onclick={() => deleteSocialLink(index)}
                          class="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                          disabled={socialLinksLoading}
                        >
                          Remove
                        </button>
                      </div>
                    {/each}
                  </div>

                  <button
                    type="button"
                    onclick={addSocialLink}
                    class="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
                    disabled={socialLinksLoading}
                  >
                    + Add Social Link
                  </button>
                </div>
              </section>

              <!-- Email Management Section -->
              <section
                class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 mt-6"
              >
                <h2
                  class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 rounded-t-lg text-gray-900 dark:text-gray-100"
                >
                  Email Addresses
                </h2>
                <div class="p-6">
                  {#if emailsError}
                    <div class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p class="text-sm text-red-600 dark:text-red-400">{emailsError}</p>
                    </div>
                  {/if}

                  {#if emailsSuccess}
                    <div class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                      <p class="text-sm text-green-600 dark:text-green-400">{emailsSuccess}</p>
                    </div>
                  {/if}

                  <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Manage your email addresses. Set which emails are public and visible on your profile.
                  </p>

                  <div class="space-y-3 mb-4">
                    {#each userEmails as email}
                      <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div class="flex items-start justify-between">
                          <div class="flex-1">
                            <div class="flex items-center gap-2">
                              <span class="text-gray-900 dark:text-gray-100">{email.email}</span>
                              {#if email.isPrimary}
                                <span class="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs rounded">Primary</span>
                              {/if}
                              {#if email.isVerified}
                                <span class="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded">Verified</span>
                              {:else}
                                <span class="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded">Unverified</span>
                              {/if}
                            </div>
                            <div class="flex items-center gap-4 mt-2">
                              <label class="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={email.isPublic}
                                  onchange={(e) => toggleEmailPublic(email.id, e.currentTarget.checked)}
                                  disabled={emailsLoading}
                                  class="rounded"
                                />
                                <span class="text-gray-600 dark:text-gray-400">Show on public profile</span>
                              </label>
                              {#if !email.isPrimary}
                                <button
                                  type="button"
                                  onclick={() => setPrimaryEmail(email.id)}
                                  class="text-sm text-brand-500 dark:text-indigo-400 hover:underline"
                                  disabled={emailsLoading}
                                >
                                  Set as primary
                                </button>
                              {/if}
                              {#if !email.isVerified}
                                <button
                                  type="button"
                                  onclick={() => { verifyingEmailId = email.id; verificationCode = ""; }}
                                  class="text-sm text-green-600 dark:text-green-400 hover:underline"
                                  disabled={emailsLoading}
                                >
                                  Verify email
                                </button>
                              {/if}
                            </div>

                            <!-- Verification Code Input -->
                            {#if verifyingEmailId === email.id && !email.isVerified}
                              <div class="mt-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-md">
                                <p class="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                  Enter the verification code sent to your email
                                </p>
                                <div class="flex gap-2">
                                  <input
                                    type="text"
                                    bind:value={verificationCode}
                                    placeholder="Verification code"
                                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 text-sm"
                                  />
                                  <button
                                    type="button"
                                    onclick={() => verifyEmail(email.id)}
                                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 text-sm"
                                    disabled={emailsLoading || !verificationCode}
                                  >
                                    Verify
                                  </button>
                                  <button
                                    type="button"
                                    onclick={() => { verifyingEmailId = ""; verificationCode = ""; }}
                                    class="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            {/if}
                          </div>
                          {#if !email.isPrimary}
                            <button
                              type="button"
                              onclick={() => deleteEmail(email.id)}
                              class="ml-4 text-red-600 dark:text-red-400 hover:text-red-700"
                              disabled={emailsLoading}
                            >
                              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                              </svg>
                            </button>
                          {/if}
                        </div>
                      </div>
                    {/each}
                  </div>

                  <!-- Add New Email Form -->
                  <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Add New Email</h3>
                    <div class="flex items-end gap-3">
                      <div class="flex-1">
                        <input
                          type="email"
                          bind:value={newEmailAddress}
                          placeholder="new-email@example.com"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>
                      <label class="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          bind:checked={newEmailPublic}
                          class="rounded"
                        />
                        <span class="text-gray-600 dark:text-gray-400">Public</span>
                      </label>
                      <button
                        type="button"
                        onclick={addNewEmail}
                        class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
                        disabled={emailsLoading || !newEmailAddress}
                      >
                        Add Email
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            {/if}
          {/if}

          <!-- Appearance Section -->
          {#if activeTab === "appearance"}
            <section
              class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            >
              <h2
                class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 rounded-t-lg text-gray-900 dark:text-gray-100"
              >
                Appearance
              </h2>
              <div class="p-6">
                <div class="space-y-4">
                  <div>
                    <label
                      class="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300"
                    >
                      Theme
                    </label>
                    <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Select your preferred theme for the interface
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Light Theme Option -->
                      <button
                        type="button"
                        onclick={() => updateTheme("light")}
                        class="p-4 border-2 rounded-lg transition-all {currentTheme ===
                        'light'
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'}"
                      >
                        <div class="flex items-center justify-between mb-2">
                          <span
                            class="font-medium text-gray-900 dark:text-gray-100"
                            >Light</span
                          >
                          {#if currentTheme === "light"}
                            <svg
                              class="w-5 h-5 text-brand-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clip-rule="evenodd"
                              />
                            </svg>
                          {/if}
                        </div>
                        <div
                          class="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400"
                        >
                          <svg
                            class="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                          </svg>
                          <span>Bright and clear</span>
                        </div>
                      </button>

                      <!-- Dark Theme Option -->
                      <button
                        type="button"
                        onclick={() => updateTheme("dark")}
                        class="p-4 border-2 rounded-lg transition-all {currentTheme ===
                        'dark'
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'}"
                      >
                        <div class="flex items-center justify-between mb-2">
                          <span
                            class="font-medium text-gray-900 dark:text-gray-100"
                            >Dark</span
                          >
                          {#if currentTheme === "dark"}
                            <svg
                              class="w-5 h-5 text-brand-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clip-rule="evenodd"
                              />
                            </svg>
                          {/if}
                        </div>
                        <div
                          class="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400"
                        >
                          <svg
                            class="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                            />
                          </svg>
                          <span>Easy on the eyes</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          {/if}

          <!-- Billing Section -->
          {#if activeTab === "billing"}
            <BillingSection />
          {/if}

          <!-- Active Sessions Section -->
          {#if activeTab === "session"}
            <section
              class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 mt-8"
            >
              <h2
                class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                Active Sessions
              </h2>
              <div class="p-6">
                {#if sessionsError}
                  <div
                    class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                  >
                    <p class="text-sm text-red-600 dark:text-red-400">
                      {sessionsError}
                    </p>
                  </div>
                {/if}

                {#if sessionsSuccess}
                  <div
                    class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
                  >
                    <p class="text-sm text-green-600 dark:text-green-400">
                      {sessionsSuccess}
                    </p>
                  </div>
                {/if}

                <div class="mb-4">
                  <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    This is a list of devices that have logged into your
                    account. Revoke any sessions that you do not recognize.
                  </p>
                  {#if sessions.filter((s) => !s.current).length > 0}
                    <button
                      type="button"
                      onclick={handleRevokeAllSessions}
                      disabled={sessionsLoading}
                      class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {sessionsLoading
                        ? "Revoking..."
                        : "Revoke All Other Sessions"}
                    </button>
                  {/if}
                </div>

                {#if sessions.length === 0}
                  <div
                    class="p-8 text-center border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <svg
                      class="w-12 h-12 mx-auto text-gray-400 mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                      No active sessions
                    </p>
                  </div>
                {:else}
                  <div class="space-y-3">
                    {#each sessions as session}
                      <div
                        class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 transition-colors {session.current
                          ? 'ring-2 ring-green-500 dark:ring-green-600'
                          : ''}"
                      >
                        <div class="flex items-start justify-between">
                          <div class="flex-1 min-w-0">
                            <!-- Device and Browser -->
                            <div class="flex items-center gap-2 mb-3">
                              <div
                                class="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg"
                              >
                                {#if session.os.includes("Windows")}
                                  <svg
                                    class="w-6 h-6 text-brand-500"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"
                                    />
                                  </svg>
                                {:else if session.os.includes("macOS") || session.os.includes("iOS")}
                                  <svg
                                    class="w-6 h-6 text-gray-700 dark:text-gray-300"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                                    />
                                  </svg>
                                {:else if session.os.includes("Ubuntu") || session.os.includes("Linux")}
                                  <svg
                                    class="w-6 h-6 text-orange-600"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M22 12.204c0-5.124-4.074-9.277-9.114-9.277S3.772 7.08 3.772 12.204s4.074 9.277 9.114 9.277S22 17.328 22 12.204zm-9.114 7.068c-3.817 0-6.914-3.146-6.914-7.068s3.097-7.068 6.914-7.068 6.914 3.146 6.914 7.068-3.097 7.068-6.914 7.068z"
                                    />
                                  </svg>
                                {:else}
                                  <svg
                                    class="w-6 h-6 text-gray-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      stroke-linecap="round"
                                      stroke-linejoin="round"
                                      stroke-width="2"
                                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                  </svg>
                                {/if}
                              </div>
                              <div>
                                <h4
                                  class="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2"
                                >
                                  {session.device}
                                  {#if session.current}
                                    <span
                                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800"
                                    >
                                      Current Session
                                    </span>
                                  {/if}
                                </h4>
                                <p
                                  class="text-sm text-gray-600 dark:text-gray-400"
                                >
                                  {session.browser} · {session.os}
                                </p>
                              </div>
                            </div>

                            <!-- Location and IP -->
                            <div
                              class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm"
                            >
                              <div class="flex items-start gap-2">
                                <svg
                                  class="w-4 h-4 text-gray-400 mt-0.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                <div>
                                  <p
                                    class="text-gray-500 dark:text-gray-400 text-xs"
                                  >
                                    Location
                                  </p>
                                  <p class="text-gray-900 dark:text-gray-100">
                                    {session.location}
                                  </p>
                                </div>
                              </div>

                              <div class="flex items-start gap-2">
                                <svg
                                  class="w-4 h-4 text-gray-400 mt-0.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                  />
                                </svg>
                                <div>
                                  <p
                                    class="text-gray-500 dark:text-gray-400 text-xs"
                                  >
                                    IP Address
                                  </p>
                                  <p
                                    class="text-gray-900 dark:text-gray-100 font-mono"
                                  >
                                    {session.ip}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <!-- Last Accessed -->
                            <div
                              class="flex items-center gap-2 mt-3 text-sm text-gray-500 dark:text-gray-400"
                            >
                              <svg
                                class="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span>Last accessed {session.lastAccessed}</span>
                            </div>
                          </div>

                          <!-- Revoke Button -->
                          <button
                            type="button"
                            onclick={() => handleRevokeSession(session.id)}
                            disabled={sessionsLoading}
                            class="ml-4 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-300 dark:border-red-800 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={session.current ? "Revoke current session and logout" : "Revoke session"}
                          >
                            {session.current ? "Revoke & Logout" : "Revoke"}
                          </button>
                        </div>
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            </section>
          {/if}

          <!-- Password Change Section -->
          {#if activeTab === "security"}
            <section
              class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            >
              <h2
                class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                Change Password
              </h2>
              <div class="p-6">
                {#if passwordError}
                  <div
                    class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                  >
                    <p class="text-sm text-red-600 dark:text-red-400">
                      {passwordError}
                    </p>
                  </div>
                {/if}

                {#if passwordSuccess}
                  <div
                    class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
                  >
                    <p class="text-sm text-green-600 dark:text-green-400">
                      {passwordSuccess}
                    </p>
                  </div>
                {/if}

                {#if !codeSent}
                  <div class="mb-4">
                    <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      To change your password, we'll send a verification code to
                      your email.
                    </p>
                    <button
                      type="button"
                      onclick={requestPasswordCode}
                      disabled={passwordLoading}
                      class="bg-indigo-600 text-white py-2 px-4 rounded-md font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-800"
                    >
                      {passwordLoading
                        ? "Sending..."
                        : "Send Verification Code"}
                    </button>
                  </div>
                {:else}
                  <form onsubmit={handleChangePassword} class="space-y-6">
                    <div>
                      <label
                        for="code"
                        class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                      >
                        Verification Code
                      </label>
                      <input
                        type="text"
                        id="code"
                        bind:value={passwordCode}
                        disabled={passwordLoading}
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                        placeholder="Enter code from email"
                        required
                      />
                    </div>

                    <div>
                      <label
                        for="newPassword"
                        class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                      >
                        New Password
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        bind:value={newPassword}
                        disabled={passwordLoading}
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                        placeholder="Min 8 characters"
                        required
                      />
                    </div>

                    <div>
                      <label
                        for="confirmPassword"
                        class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                      >
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        bind:value={confirmPassword}
                        disabled={passwordLoading}
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                        placeholder="Re-enter new password"
                        required
                      />
                    </div>

                    <div class="flex gap-3">
                      <button
                        type="submit"
                        disabled={passwordLoading}
                        class="bg-green-600 text-white py-2 px-4 rounded-md font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-800"
                      >
                        {passwordLoading ? "Changing..." : "Change Password"}
                      </button>
                      <button
                        type="button"
                        onclick={() => {
                          codeSent = false;
                          passwordCode = "";
                          newPassword = "";
                          confirmPassword = "";
                          passwordError = "";
                        }}
                        disabled={passwordLoading}
                        class="py-2 px-4 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                {/if}
              </div>
            </section>

            <!-- SSH and GPG Keys Section -->
            <section
              class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 mt-8"
            >
              <h2
                class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                SSH and GPG Keys
              </h2>
              <div class="p-6">
                {#if keysError}
                  <div
                    class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                  >
                    <p class="text-sm text-red-600 dark:text-red-400">
                      {keysError}
                    </p>
                  </div>
                {/if}

                {#if keysSuccess}
                  <div
                    class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
                  >
                    <p class="text-sm text-green-600 dark:text-green-400">
                      {keysSuccess}
                    </p>
                  </div>
                {/if}

                <!-- SSH Keys -->
                <div class="mb-8">
                  <div class="flex items-center justify-between mb-4">
                    <div>
                      <h3
                        class="text-lg font-medium text-gray-900 dark:text-gray-100"
                      >
                        SSH Keys
                      </h3>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Add SSH keys to authenticate Git operations without a
                        password
                      </p>
                    </div>
                    <button
                      type="button"
                      onclick={() => (showAddSSHKey = !showAddSSHKey)}
                      class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors text-sm"
                    >
                      Add SSH Key
                    </button>
                  </div>

                  <!-- Add SSH Key Form -->
                  {#if showAddSSHKey}
                    <div
                      class="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900"
                    >
                      <div class="space-y-4">
                        <div>
                          <label
                            for="sshKeyTitle"
                            class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                          >
                            Title
                          </label>
                          <input
                            type="text"
                            id="sshKeyTitle"
                            bind:value={newSSHKeyTitle}
                            placeholder="e.g., Personal Laptop"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                          />
                        </div>
                        <div>
                          <label
                            for="sshKeyContent"
                            class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                          >
                            Key
                          </label>
                          <textarea
                            id="sshKeyContent"
                            bind:value={newSSHKeyContent}
                            rows="4"
                            placeholder="Begins with 'ssh-rsa', 'ssh-ed25519', etc."
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                          ></textarea>
                        </div>
                        <div class="flex gap-2">
                          <button
                            type="button"
                            onclick={handleAddSSHKey}
                            disabled={keysLoading}
                            class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {keysLoading ? "Adding..." : "Add Key"}
                          </button>
                          <button
                            type="button"
                            onclick={() => {
                              showAddSSHKey = false;
                              newSSHKeyTitle = "";
                              newSSHKeyContent = "";
                            }}
                            class="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors text-sm dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  {/if}

                  <!-- SSH Keys List -->
                  {#if sshKeys.length === 0}
                    <div
                      class="p-8 text-center border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <svg
                        class="w-12 h-12 mx-auto text-gray-400 mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                        />
                      </svg>
                      <p class="text-sm text-gray-600 dark:text-gray-400">
                        No SSH keys configured
                      </p>
                    </div>
                  {:else}
                    <div class="space-y-3">
                      {#each sshKeys as key}
                        <div
                          class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                        >
                          <div class="flex items-start justify-between">
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center gap-2 mb-2">
                                <h4
                                  class="font-medium text-gray-900 dark:text-gray-100"
                                >
                                  {key.title}
                                </h4>
                                <span
                                  class="text-xs text-gray-500 dark:text-gray-400"
                                >
                                  Added {key.createdAt}
                                </span>
                              </div>
                              <div class="mb-2">
                                <p
                                  class="text-sm text-gray-600 dark:text-gray-400 font-mono truncate"
                                >
                                  {key.fingerprint}
                                </p>
                              </div>
                              <details class="text-sm">
                                <summary
                                  class="cursor-pointer text-brand-500 dark:text-blue-400 hover:underline"
                                >
                                  View key
                                </summary>
                                <pre
                                  class="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-xs overflow-x-auto">{key.key}</pre>
                              </details>
                            </div>
                            <button
                              type="button"
                              onclick={() => handleDeleteSSHKey(key.id)}
                              disabled={keysLoading}
                              class="ml-4 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete key"
                            >
                              <svg
                                class="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      {/each}
                    </div>
                  {/if}
                </div>

                <!-- GPG Keys -->
                <div>
                  <div class="flex items-center justify-between mb-4">
                    <div>
                      <h3
                        class="text-lg font-medium text-gray-900 dark:text-gray-100"
                      >
                        GPG Keys
                      </h3>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Add GPG keys to sign your commits and tags
                      </p>
                    </div>
                    <button
                      type="button"
                      onclick={() => (showAddGPGKey = !showAddGPGKey)}
                      class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors text-sm"
                    >
                      Add GPG Key
                    </button>
                  </div>

                  <!-- Add GPG Key Form -->
                  {#if showAddGPGKey}
                    <div
                      class="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900"
                    >
                      <div class="space-y-4">
                        <div>
                          <label
                            for="gpgKeyTitle"
                            class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                          >
                            Title
                          </label>
                          <input
                            type="text"
                            id="gpgKeyTitle"
                            bind:value={newGPGKeyTitle}
                            placeholder="e.g., Main GPG Key"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                          />
                        </div>
                        <div>
                          <label
                            for="gpgKeyContent"
                            class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                          >
                            Key
                          </label>
                          <textarea
                            id="gpgKeyContent"
                            bind:value={newGPGKeyContent}
                            rows="6"
                            placeholder="Begins with '-----BEGIN PGP PUBLIC KEY BLOCK-----'"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                          ></textarea>
                        </div>
                        <div class="flex gap-2">
                          <button
                            type="button"
                            onclick={handleAddGPGKey}
                            disabled={keysLoading}
                            class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {keysLoading ? "Adding..." : "Add Key"}
                          </button>
                          <button
                            type="button"
                            onclick={() => {
                              showAddGPGKey = false;
                              newGPGKeyTitle = "";
                              newGPGKeyContent = "";
                            }}
                            class="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors text-sm dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  {/if}

                  <!-- GPG Keys List -->
                  {#if gpgKeys.length === 0}
                    <div
                      class="p-8 text-center border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <svg
                        class="w-12 h-12 mx-auto text-gray-400 mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                      <p class="text-sm text-gray-600 dark:text-gray-400">
                        No GPG keys configured
                      </p>
                    </div>
                  {:else}
                    <div class="space-y-3">
                      {#each gpgKeys as key}
                        <div
                          class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                        >
                          <div class="flex items-start justify-between">
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center gap-2 mb-2">
                                <h4
                                  class="font-medium text-gray-900 dark:text-gray-100"
                                >
                                  {key.title}
                                </h4>
                                <span
                                  class="text-xs text-gray-500 dark:text-gray-400"
                                >
                                  Added {key.createdAt}
                                </span>
                              </div>
                              <div class="mb-2">
                                <p
                                  class="text-sm text-gray-600 dark:text-gray-400 font-mono"
                                >
                                  Key ID: {key.keyId}
                                </p>
                              </div>
                              <details class="text-sm">
                                <summary
                                  class="cursor-pointer text-brand-500 dark:text-blue-400 hover:underline"
                                >
                                  View public key
                                </summary>
                                <pre
                                  class="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-xs overflow-x-auto">{key.publicKey}</pre>
                              </details>
                            </div>
                            <button
                              type="button"
                              onclick={() => handleDeleteGPGKey(key.id)}
                              disabled={keysLoading}
                              class="ml-4 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete key"
                            >
                              <svg
                                class="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      {/each}
                    </div>
                  {/if}
                </div>
              </div>
            </section>

            <!-- 2FA_SECTION_PLACEHOLDER -->

            <!-- Two-Factor Authentication Section -->
            <section
              class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 mt-8"
            >
              <h2
                class="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                Two-Factor Authentication
              </h2>
              <div class="p-6">
                {#if twoFactorError}
                  <div
                    class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                  >
                    <p class="text-sm text-red-600 dark:text-red-400">
                      {twoFactorError}
                    </p>
                  </div>
                {/if}

                {#if twoFactorSuccess}
                  <div
                    class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
                  >
                    <p class="text-sm text-green-600 dark:text-green-400">
                      {twoFactorSuccess}
                    </p>
                  </div>
                {/if}

                <!-- 2FA Status -->
                <div class="flex items-center justify-between mb-6">
                  <div>
                    <div class="flex items-center gap-2">
                      <h3
                        class="text-lg font-medium text-gray-900 dark:text-gray-100"
                      >
                        Two-Factor Authentication
                      </h3>
                      {#if twoFactorEnabled}
                        <span
                          class="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full"
                        >
                          Enabled
                        </span>
                      {/if}
                    </div>
                    <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {twoFactorEnabled
                        ? "Your account is protected with an extra layer of security"
                        : "Add an extra layer of security to your account"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onclick={() => {
                      if (twoFactorEnabled) {
                        show2FASetup = !show2FASetup;
                      } else {
                        handleEnableTwoFactor();
                      }
                    }}
                    class={twoFactorEnabled
                      ? "px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                      : "px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                    }
                    disabled={twoFactorLoading}
                  >
                    {twoFactorLoading ? "Loading..." : (twoFactorEnabled ? "Disable 2FA" : "Enable 2FA")}
                  </button>
                </div>

                <!-- 2FA Setup Form -->
                {#if show2FASetup && !twoFactorEnabled}
                  <div
                    class="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900"
                  >
                    <div class="space-y-6">
                      <div>
                        <p
                          class="text-sm text-gray-700 dark:text-gray-300 mb-4"
                        >
                          Scan this QR code with your authenticator app (like
                          Google Authenticator, Authy, or Microsoft
                          Authenticator):
                        </p>

                        <!-- QR Code Display -->
                        {#if twoFactorQrCode}
                          <div class="flex flex-col items-center mb-4">
                            <div
                              class="p-4 bg-white rounded-lg border border-gray-300 mb-4"
                            >
                              <img
                                src={twoFactorQrCode}
                                alt="QR Code for 2FA setup"
                                class="w-48 h-48"
                              />
                            </div>
                            <p
                              class="text-sm text-gray-600 dark:text-gray-400 text-center mb-4"
                            >
                              Scan the QR code with your authenticator app
                            </p>
                          </div>
                        {/if}

                        <!-- Secret Key (for manual entry) -->
                        {#if twoFactorSecret}
                          <div class="mb-4">
                            <label
                              class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                            >
                              Secret Key (for manual entry)
                            </label>
                            <div class="flex items-center gap-2">
                              <code
                                class="flex-1 p-2 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700 font-mono text-sm"
                              >
                                {twoFactorSecret}
                              </code>
                              <button
                                type="button"
                                onclick={() => copyToClipboard(twoFactorSecret)}
                                class="px-3 py-2 text-sm text-brand-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                        {/if}

                        <!-- Verification Code Input -->
                        <div>
                          <label
                            for="totpCode"
                            class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                          >
                            Enter 6-digit verification code
                          </label>
                          <input
                            type="text"
                            id="totpCode"
                            bind:value={otpVerificationCode}
                            maxlength="6"
                            pattern="[0-9]*"
                            inputmode="numeric"
                            placeholder="123456"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 text-center text-xl tracking-widest"
                          />
                        </div>

                        <div class="flex gap-2">
                          <button
                            type="button"
                            onclick={enable2FA}
                            disabled={twoFactorLoading}
                            class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {twoFactorLoading
                              ? "Verifying..."
                              : "Verify and Enable 2FA"}
                          </button>
                          <button
                            type="button"
                            onclick={() => {
                              show2FASetup = false;
                              twoFactorQrCode = "";
                              twoFactorSecret = "";
                              totpCode = "";
                            }}
                            class="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors text-sm dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                {/if}

                <!-- 2FA Disable Form -->
                {#if show2FASetup && twoFactorEnabled}
                  <div
                    class="mb-4 p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20"
                  >
                    <div class="space-y-4">
                      <div class="flex items-start">
                        <svg
                          class="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.196 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                        <div>
                          <h4
                            class="font-medium text-red-800 dark:text-red-300"
                          >
                            Disable Two-Factor Authentication
                          </h4>
                          <p
                            class="text-sm text-red-700 dark:text-red-400 mt-1"
                          >
                            Disabling 2FA reduces the security of your account.
                            Make sure this is what you want to do.
                          </p>
                        </div>
                      </div>

                      <div>
                        <label
                          for="disableTotpCode"
                          class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                        >
                          Enter 6-digit verification code to disable
                        </label>
                        <input
                          type="text"
                          id="disableTotpCode"
                          bind:value={disableTotpCode}
                          maxlength="6"
                          pattern="[0-9]*"
                          inputmode="numeric"
                          placeholder="123456"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                        />
                      </div>

                      <div class="flex gap-2">
                        <button
                          type="button"
                          onclick={disable2FA}
                          disabled={twoFactorLoading}
                          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          {twoFactorLoading ? "Disabling..." : "Disable 2FA"}
                        </button>
                        <button
                          type="button"
                          onclick={() => {
                            show2FASetup = false;
                            disableTotpCode = "";
                          }}
                          class="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors text-sm dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                {/if}

                <!-- Recovery Codes -->
                {#if twoFactorEnabled && recoveryCodes.length > 0}
                  <div
                    class="mt-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900"
                  >
                    <div class="flex items-center justify-between mb-4">
                      <div>
                        <h4
                          class="font-medium text-gray-900 dark:text-gray-100"
                        >
                          Recovery Codes
                        </h4>
                        <p
                          class="text-sm text-gray-600 dark:text-gray-400 mt-1"
                        >
                          Save these recovery codes in a safe place. You can use
                          them to access your account if you lose your
                          authenticator device.
                        </p>
                      </div>
                      <button
                        type="button"
                        onclick={() => (showRecoveryCodes = !showRecoveryCodes)}
                        class="text-sm text-brand-500 dark:text-blue-400 hover:underline font-medium"
                      >
                        {showRecoveryCodes ? "Hide" : "Show"} Codes
                      </button>
                    </div>

                    {#if showRecoveryCodes}
                      <div class="mt-4">
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                          {#each recoveryCodes as code, index}
                            <div
                              class="p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded text-center font-mono text-sm"
                            >
                              {code}
                            </div>
                          {/each}
                        </div>
                        <div class="flex gap-2">
                          <button
                            type="button"
                            onclick={() => copyRecoveryCodes()}
                            class="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                          >
                            Copy All
                          </button>
                          <button
                            type="button"
                            onclick={generateNewRecoveryCodes}
                            disabled={twoFactorLoading}
                            class="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Generate New Codes
                          </button>
                          <button
                            type="button"
                            onclick={downloadRecoveryCodes}
                            class="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:border-gray-600"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            </section>
          {/if}
        {/if}
      </main>
    </div>
  </div>
</div>

<style>
  .settings-link {
    display: block;
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 4px;
    font-weight: 500;
    color: #6b7280;
    transition:
      background-color 0.15s,
      color 0.15s;
    cursor: pointer;
  }
  .settings-link:hover {
    color: #374151;
    background-color: #f3f4f6;
  }
  .settings-link.active {
    color: #1f2937;
    background-color: #e5e7eb;
  }
</style>
